// Package imports:
import React, { useState, useEffect } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserCircle } from '@fortawesome/pro-solid-svg-icons';
import { faChevronDown, faSignOut } from '@fortawesome/pro-light-svg-icons';
// Style imports:
// import './NavigationBar.scss';
// Component imports:
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import NavBarLogo from './NavBarLogo';
import AutoComplete from '../common/AutoComplete';
import About from './About';
import AboutPrivateData from './AboutPrivateData';
import Navbar from 'react-bootstrap/Navbar';
import Nav from 'react-bootstrap/Nav';
import NavDropdown from 'react-bootstrap/NavDropdown';
// Service imports:
import { isUserLoggedIn } from '../../services/loginUtils';
import { getLastTradingDay, isEnvironmentProduction } from '../../services/utils';
// Action imports:
import { logOut } from '../../redux-store/actions/loginActions';
// Type imports:
import { RootState } from '../../redux-store/reducers';

type ReduxProps = ConnectedProps<typeof connector>;
type Props = ReduxProps;

const NavigationBar: React.FC<Props> = ({
    isLoggedIn,
    hasEmsToken,
    accessTokenV2,
    loginUserName,
    currentTradingDay,
    dayRange,
    logOut
}) => {
    const [ showAboutModal, setShowAboutModal ] = useState(false);
    const [ showPrivateDataModal, setShowPrivateDataModal ] = useState(false);

    const getDayDependingOnPath = () => {
        const currentPath = window.location.pathname;
        const DAY_RANGE_PATHS = ['alertsall'];
        const TRADING_DAY_PATHS = ['sigti', 'shares', 'allbookmarks'];
        // The /alertsall path uses day range. While /shares and /sigti use current day. /bookmarks doesnt use days (use default).
        // Day range.
        if (DAY_RANGE_PATHS.some(pathMatch => currentPath.includes(pathMatch))) {
            return dayRange.fromDate;
        }
        // Single day.
        else if (TRADING_DAY_PATHS.some(pathMatch => currentPath.includes(pathMatch))) {
            return currentTradingDay;
        }
        // Default day (last trading day)
        else {
            return getLastTradingDay();
        }
    }

    const displayExtraNavigationBarItems = () => {
        if (isLoggedIn) {
            const day = getDayDependingOnPath();
            return (
                <>
                    {/* <Navbar.Toggle aria-controls='ExtraNavigationItemsNav'>
                        <i className='fas fa-bars' />
                    </Navbar.Toggle> */}
                    <Navbar.Collapse className='MiddleNavCollapse'>
                        <Nav className='NavLinkWrapper'>
                            {
                                [{
                                    title: 'Overview',
                                    link: `/shares?tradingDay=${day}`,
                                    path: `/shares`
                                }, {
                                    title: 'Alerts',
                                    link: `/alertsall?fromDate=${day}&toDate=${day}`,
                                    path: `/alertsall`
                                }, {
                                    title: 'Bookmarks',
                                    link: `/allbookmarks?tradingDay=${day}`,
                                    path: `/allbookmarks`
                                }, {
                                    title: 'Reports',
                                    link: `/reports?tradingDay=${day}`,
                                    path: `/reports`
                                }].map(({ title, link, path }, index) => (
                                    <Nav.Link
                                        key={title}
                                        href={link}
                                        className='NavLink'
                                    >
                                        {
                                            (window.location.pathname === path)
                                            ? <div className='ActiveLinkDiv' />
                                            : null
                                        }
                                        {title}
                                    </Nav.Link>
                                ))
                            }
                            <AutoComplete />
                        </Nav>
                    </Navbar.Collapse>
                    <Nav className='FarRightNav'>
                        <NavDropdown
                            align="end"
                            id='NavigationBarDropdown'
                            title={
                                <>
                                    <OverlayTrigger
                                        rootClose
                                        delay={{ show: 100, hide: 100 }}
                                        trigger={['hover', 'focus']}
                                        placement='left'
                                        overlay={(
                                            <Tooltip id='UserCircleTooltip'>
                                                {
                                                    (hasEmsToken)
                                                    ? 'Private data'
                                                    : 'No Private data'
                                                }
                                            </Tooltip>
                                        )}
                                    >
                                        <FontAwesomeIcon
                                            className={'UserCircleIcon' + ((hasEmsToken) ? ' HasPrivate' : '')}
                                            icon={faUserCircle}
                                        />
                                    </OverlayTrigger>
                                    <span className='UsernameSpan'>
                                        {loginUserName}
                                    </span>
                                    <FontAwesomeIcon
                                        icon={faChevronDown}
                                    />
                                </>
                            }
                        >
                            <NavDropdown.Item href='/alerts'>Alert Configuration</NavDropdown.Item>
                            <NavDropdown.Item href='https://kodihq.atlassian.net/wiki/x/fgC3H' target='_blank'>User guide</NavDropdown.Item>
                            <NavDropdown.Item
                                onClick={() => {
                                    logOut(accessTokenV2)
                                    window.location.href = '/login';
                                }}
                            >
                                <FontAwesomeIcon
                                    icon={faSignOut}
                                    className='LogOutIcon'
                                />
                                Log Out
                            </NavDropdown.Item>
                        </NavDropdown>
                    </Nav>
                </>
            )
        }
    }

    const day = getDayDependingOnPath();
    return (
        <>
            <Navbar
                id='NavigationBar'
                expand='lg'
                collapseOnSelect
            >
                {
                    (!isEnvironmentProduction())
                    ? <p className='StagingBanner'>ATHUGIÐ. ÞETTA ER STAGING</p>
                    : null
                }
                <Navbar.Brand
                    className='NavbarLogo'
                    href={`/shares?tradingDay=${day}`}
                >
                    <NavBarLogo
                        width={174}
                        height={46}
                    />
                </Navbar.Brand>
                {displayExtraNavigationBarItems()}
            </Navbar>
            {/* Modals */}
            <About
                showModal={showAboutModal}
                closeModal={() => setShowAboutModal(false)}
            />
            <AboutPrivateData
                showModal={showPrivateDataModal}
                closeModal={() => setShowPrivateDataModal(false)}
            />
        </>
    );
}

function mapStateToProps({ loginReducer, dashboardReducer }: RootState) {
    const { accessTokenV2, loginUserName } = loginReducer;
    const { isLoggedIn, hasEmsToken } = isUserLoggedIn(loginReducer);
    const { currentTradingDay, dayRange } = dashboardReducer;
    
    return {
        isLoggedIn,
        hasEmsToken,
        accessTokenV2,
        loginUserName,
        currentTradingDay,
        dayRange
    }
}

const connector = connect(mapStateToProps, { logOut });
export default connector(NavigationBar);